import { useTranslation } from 'react-i18next';
import zoomPlugin from 'chartjs-plugin-zoom';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Bar, Line, Pie } from 'react-chartjs-2';
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Tooltip,
} from 'chart.js';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import NoData from '../components/NoData';
import SaleReportsDialog from '../components/SaleReportsDialog';
import {
  clearOrderStatisticsEvents,
  fetchOrderStatisticsEvents,
  fetchOrganizerTopPerformersStatistics,
  fetchOrganizerTicketSalesStatistics,
  setOrderStatisticsDateRange,
} from '../redux/slices/statisticsSlice';
import { fetchOrganizers } from '../redux/slices/organizersSlice';
import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm';

ChartJS.register(
  ArcElement,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  zoomPlugin
);

const DATE_FORMAT = 'YYYY-MM-DD';
const ONE_DAY = 24 * 60 * 60 * 1000;
const SEVEN_DAYS = 7 * ONE_DAY;
const FOURTEEN_DAYS = 14 * ONE_DAY;
const MAX_RANGE_MONTHS = 2;
const TOP_PLAYS_PAGE_SIZE = 10;
const TOP_PLAYS_LABEL_MAX_LENGTH = 28;
const EVENT_SELECT_PAGE_SIZE = 100;

const channelKeys = ['online', 'cashier', 'organizer'];

const colors = ['#E5484D', '#2F9BFF', '#F59E0B', '#8B5CF6', '#10B981'];
const channelColors = {
  online: '#E5484D',
  cashier: '#2F9BFF',
  organizer: '#F59E0B',
  total: '#8B5CF6',
};

const getSeriesColor = (channel, index = 0) =>
  channelColors[String(channel).toLowerCase()] || colors[index % colors.length];

const normalizeChartSectionPoints = (labels, section, valueKey) => {
  if (!Array.isArray(labels) || !section || typeof section !== 'object') return [];

  return channelKeys.flatMap((channel) => {
    const values = section[channel];

    if (!Array.isArray(values)) return [];

    return values
      .map((value, index) => {
        const date = labels[index];

        return {
          channel,
          date: dayjs(date).isValid() ? dayjs(date).format(DATE_FORMAT) : null,
          [valueKey]: Number(value),
        };
      })
      .filter((point) => point.date && Number.isFinite(point[valueKey]));
  });
};

const normalizeTimeSeriesPoints = (payload, chartKey, valueKey, { singleSeries = false } = {}) => {
  const labels = payload?.chart?.labels;
  const values = payload?.chart?.[chartKey];

  if (singleSeries && Array.isArray(labels) && Array.isArray(values)) {
    return values
      .map((value, index) => {
        const date = labels[index];

        return {
          channel: 'total',
          date: dayjs(date).isValid() ? dayjs(date).format(DATE_FORMAT) : null,
          [valueKey]: Number(value),
        };
      })
      .filter((point) => point.date && Number.isFinite(point[valueKey]));
  }

  return normalizeChartSectionPoints(labels, values, valueKey);
};

const normalizePoints = (payload) => normalizeTimeSeriesPoints(payload, 'tickets', 'tickets');

const normalizeIncomePoints = (payload) => normalizeTimeSeriesPoints(payload, 'income', 'income');

const normalizeComissionPoints = (payload) =>
  normalizeTimeSeriesPoints(payload, 'comission', 'comission', { singleSeries: true });

const normalizeOrderPoints = (payload) => {
  return normalizeTimeSeriesPoints(payload, 'orders', 'orders');
};

const buildTimeSeriesRows = (points, valueKey) => {
  const totalsByChannelAndDate = points.reduce((acc, point) => {
    const channel = point.channel || 'total';
    const value = Number(point[valueKey]);

    if (!point.date || !channel || !Number.isFinite(value)) return acc;

    const key = `${channel}:${point.date}`;
    const previous = acc.get(key) ?? { channel, date: point.date, [valueKey]: 0 };

    acc.set(key, {
      ...previous,
      [valueKey]: previous[valueKey] + value,
    });
    return acc;
  }, new Map());

  const rows = Array.from(totalsByChannelAndDate.values()).sort((a, b) => {
    const dateOrder = dayjs(a.date).valueOf() - dayjs(b.date).valueOf();
    if (dateOrder !== 0) return dateOrder;
    return String(a.channel).localeCompare(String(b.channel));
  });

  const labels = Array.from(new Set(rows.map((row) => row.date))).sort(
    (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()
  );

  return { rows, labels };
};

const getDateDomain = (labels) => {
  if (labels.length === 0) return { minDate: null, maxDate: null };

  const labelTimestamps = labels.map((label) => dayjs(label).valueOf());
  const minDate = Math.min(...labelTimestamps);
  const maxDate = labels.length === 1 ? Math.max(...labelTimestamps) + ONE_DAY : Math.max(...labelTimestamps);

  return { minDate, maxDate };
};

const buildTimeSeriesDatasets = ({
  rows,
  labels,
  valueKey,
  t,
  isCashier,
  filterChannel,
  getLabel,
  datasetOptions = {},
}) => {
  const channels = getChartChannels(rows, isCashier, filterChannel);

  return {
    datasets: channels.map((channel, index) => {
      const color = getSeriesColor(channel, index);
      const pointsByDate = rows
        .filter((row) => row.channel === channel)
        .reduce((acc, row) => acc.set(row.date, row[valueKey]), new Map());

      return {
        label: getLabel ? getLabel(channel) : formatChannel(channel, t),
        data: labels.map((date) => ({
          x: date,
          y: pointsByDate.get(date) ?? 0,
        })),
        borderColor: color,
        backgroundColor: color,
        pointBackgroundColor: color,
        pointBorderColor: color,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 2,
        tension: 0.25,
        ...datasetOptions,
      };
    }),
  };
};

const truncateChartLabel = (label, maxLength = TOP_PLAYS_LABEL_MAX_LENGTH) => {
  const value = String(label || '');

  if (value.length <= maxLength) return value;

  return `${value.slice(0, maxLength - 3)}...`;
};

const getSuggestedYAxisMax = (values) => {
  const maxValue = Math.max(0, ...values.filter((value) => Number.isFinite(value)));

  if (maxValue === 0) return 10;

  return Math.ceil(maxValue * 1.1);
};

const getChartChannels = (rows, isCashier, filterChannel) => {
  const channels = Array.from(new Set(rows.map((row) => row.channel)));
  const filteredChannels = filterChannel ? channels.filter(filterChannel) : channels;

  if (!isCashier) return filteredChannels;

  return filteredChannels.filter((channel) => String(channel).toUpperCase() === 'CASHIER');
};

const getNumber = (value) => {
  const number = Number(value);

  return Number.isFinite(number) ? number : 0;
};

const formatCount = (value) => getNumber(value).toLocaleString();

const getLocaleTag = (i18n) => ((i18n.resolvedLanguage || i18n.language || 'ro') === 'en' ? 'en-GB' : 'ro-RO');

const formatMoney = (value, currency = 'RON', i18n) =>
  new Intl.NumberFormat(getLocaleTag(i18n), {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(getNumber(value));

const getFilteredTotal = (totals, channels) => {
  if (!totals || typeof totals !== 'object') return getNumber(totals);

  const hasChannelValues = channels.some((channel) => totals[channel] !== undefined);

  if (!hasChannelValues) return getNumber(totals.total);

  return channels.reduce((sum, channel) => sum + getNumber(totals[channel]), 0);
};

const getTopPlayRows = (payload) => {
  const rows = Array.isArray(payload?.topPlays) ? payload.topPlays : [];

  return rows
    .map((row) => ({
      title: row.title,
      tickets: Number(row.tickets),
      income: Number(row.income),
    }))
    .filter((row) => row.title && (Number.isFinite(row.tickets) || Number.isFinite(row.income)));
};

const getTopEventRows = (payload) => {
  const rows = Array.isArray(payload?.topEvents) ? payload.topEvents : [];

  return rows
    .map((row) => {
      const eventDate = dayjs(row.date);

      return {
        title: eventDate.isValid() ? `${row.title} - ${eventDate.format('DD/MM/YYYY HH:mm')}` : row.title,
        tickets: Number(row.tickets),
        income: Number(row.income),
      };
    })
    .filter((row) => row.title && (Number.isFinite(row.tickets) || Number.isFinite(row.income)));
};

const getCashierPaymentMethodRows = (payload) => {
  const paymentMethods = payload?.cashierPaymentMethods;

  if (!paymentMethods || typeof paymentMethods !== 'object') return [];

  const amounts =
    paymentMethods.income && typeof paymentMethods.income === 'object' ? paymentMethods.income : paymentMethods;

  return [
    { method: 'pos', amount: Number(amounts.pos) },
    { method: 'cash', amount: Number(amounts.cash) },
  ].filter((row) => Number.isFinite(row.amount));
};

const formatChannel = (channel, t) => {
  const value = String(channel || 'Unknown');
  const normalized = value.toUpperCase();

  if (normalized === 'ONLINE') return t('Online');
  if (normalized === 'OFFLINE') return t('Offline Sales');
  if (normalized === 'CASHIER') return t('Cashier');
  if (normalized === 'ORGANIZER') return t('Organizer');

  return t(value);
};

const getEventLabel = (event) => {
  return `${event.id} - ${event.label || event.title}`;
};

const getEventSalesStart = (event) => {
  const value = event?.createdAt;

  return value && dayjs(value).isValid() ? dayjs(value).startOf('day') : null;
};

const getEventSalesEnd = (event) => {
  if (!event?.date || Number(event?.has_event_date) === 0) return null;

  return dayjs(event.date).isValid() ? dayjs(event.date).endOf('day') : null;
};

const maxDay = (a, b) => {
  if (!a) return b || null;
  if (!b) return a;
  return a.isAfter(b, 'day') ? a : b;
};

const minDay = (a, b) => {
  if (!a) return b || null;
  if (!b) return a;
  return a.isBefore(b, 'day') ? a : b;
};

function StatisticsLineChart({
  title,
  type = 'line',
  data,
  options,
  isLoading,
  isEmpty,
  minDate,
  maxDate,
  domainKey,
  action,
}) {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current || isEmpty || minDate === null || maxDate === null) return;

    const chart = chartRef.current;

    const frame = requestAnimationFrame(() => {
      if (!chart.canvas || chart.ctx === null || chart._destroyed) return;

      chart.options.scales.x.min = minDate;
      chart.options.scales.x.max = maxDate;
      chart.update('none');
      chart.resetZoom();

      const today = dayjs().endOf('day').valueOf();
      const fullMax = Math.min(maxDate, today);
      const range = fullMax - minDate;
      const targetMax = fullMax;
      const targetMin = range > FOURTEEN_DAYS ? fullMax - FOURTEEN_DAYS : minDate;

      chart.zoomScale('x', {
        min: targetMin,
        max: targetMax,
      });
    });

    return () => cancelAnimationFrame(frame);
  }, [domainKey, isEmpty, maxDate, minDate]);

  const panDays = (days) => {
    const chart = chartRef.current;
    if (!chart || !chart.canvas || chart.ctx === null || chart._destroyed) return;

    const scale = chart.scales.x;
    if (!scale) return;

    const visibleRange = scale.max - scale.min;
    const msPerPixel = visibleRange / scale.width;
    const msToMove = days * ONE_DAY;
    const pixelsToMove = msToMove / msPerPixel;

    chart.pan({ x: pixelsToMove });
  };

  const zoomIn = () => {
    const chart = chartRef.current;
    if (!chart || !chart.canvas || chart.ctx === null || chart._destroyed) return;

    chart.zoom(1.2);
  };

  const zoomOut = () => {
    const chart = chartRef.current;
    if (!chart || !chart.canvas || chart.ctx === null || chart._destroyed) return;

    chart.zoom(0.8);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">{title}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {action}
          {isLoading && <CircularProgress size={24} />}
          <IconButton onClick={() => panDays(1)} disabled={isEmpty}>
            <ChevronLeftIcon />
          </IconButton>
          <IconButton onClick={() => panDays(-1)} disabled={isEmpty}>
            <ChevronRightIcon />
          </IconButton>
          <IconButton onClick={zoomIn} disabled={isEmpty}>
            <ZoomInIcon />
          </IconButton>
          <IconButton onClick={zoomOut} disabled={isEmpty}>
            <ZoomOutIcon />
          </IconButton>
        </Box>
      </Box>

      {!isLoading && isEmpty ? (
        <NoData />
      ) : (
        <Box sx={{ height: 420, width: '100%' }}>
          {type === 'bar' ? (
            <Bar ref={chartRef} options={options} data={data} />
          ) : (
            <Line ref={chartRef} options={options} data={data} />
          )}
        </Box>
      )}
    </Paper>
  );
}

function TopPerformersChart({ title, rows, sortBy, onSortByChange, visibleCount, onLoadMore, isLoading }) {
  const { t, i18n } = useTranslation();
  const isIncomeSort = sortBy === 'income';
  const visibleRows = rows.slice(0, visibleCount);
  const hasMore = visibleCount < rows.length;

  const chartData = useMemo(
    () => ({
      labels: visibleRows.map((row) => row.title),
      datasets: [
        {
          label: isIncomeSort ? t('Income') : t('Tickets'),
          data: visibleRows.map((row) => (isIncomeSort ? row.income : row.tickets)),
          backgroundColor: '#D53541',
          borderColor: '#D53541',
          borderWidth: 1,
          borderRadius: 4,
          maxBarThickness: 28,
        },
      ],
    }),
    [isIncomeSort, t, visibleRows]
  );

  const chartOptions = useMemo(
    () => ({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          min: 0,
          beginAtZero: true,
          ticks: {
            color: 'white',
            precision: 0,
            callback: (value) => (isIncomeSort ? formatMoney(value, 'RON', i18n) : value),
          },
          grid: { color: 'rgba(182, 186, 182, 0.3)' },
          border: { display: false },
        },
        y: {
          ticks: {
            color: 'white',
            autoSkip: false,
            callback: function (value) {
              return truncateChartLabel(this.getLabelForValue(value));
            },
          },
          grid: { display: false },
          border: { display: false },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            title: (items) => items[0]?.label ?? '',
            label: (ctx) =>
              isIncomeSort
                ? `${ctx.dataset.label}: ${formatMoney(ctx.parsed.x, 'RON', i18n)}`
                : `${ctx.dataset.label}: ${ctx.formattedValue}`,
          },
        },
      },
    }),
    [i18n, isIncomeSort]
  );

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Typography variant="h5">{title}</Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={sortBy}
          onChange={(_, value) => {
            if (value) onSortByChange(value);
          }}
        >
          <ToggleButton value="income">{t('Income')}</ToggleButton>
          <ToggleButton value="tickets">{t('Tickets')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {!isLoading && visibleRows.length === 0 ? (
        <NoData />
      ) : (
        <Box sx={{ height: Math.max(360, visibleRows.length * 42 + 90), width: '100%' }}>
          <Bar options={chartOptions} data={chartData} />
        </Box>
      )}

      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="outlined" onClick={onLoadMore}>
            {t('Load more')}
          </Button>
        </Box>
      )}
    </Paper>
  );
}

function CashierPaymentMethodsPieChart({ rows, isLoading }) {
  const { t, i18n } = useTranslation();
  const totalAmount = rows.reduce((sum, row) => sum + row.amount, 0);

  const chartData = useMemo(
    () => ({
      labels: rows.map((row) => (row.method === 'pos' ? t('POS') : t('Cash'))),
      datasets: [
        {
          data: rows.map((row) => row.amount),
          backgroundColor: ['#E5484D', '#2F9BFF'],
          borderColor: '#1E1E1E',
          borderWidth: 2,
        },
      ],
    }),
    [rows, t]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: 'white',
            usePointStyle: true,
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const value = Number(ctx.parsed || 0);
              const percentage = totalAmount ? ((value / totalAmount) * 100).toFixed(1) : '0.0';

              return `${ctx.label}: ${formatMoney(value, 'RON', i18n)} (${percentage}%)`;
            },
          },
        },
      },
    }),
    [i18n, totalAmount]
  );

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Typography variant="h5">{t('Cashier income by payment method')}</Typography>
        {isLoading && <CircularProgress size={24} />}
      </Box>

      {!isLoading && totalAmount === 0 ? (
        <NoData />
      ) : (
        <Box sx={{ height: 360, width: '100%' }}>
          <Pie options={chartOptions} data={chartData} />
        </Box>
      )}
    </Paper>
  );
}

function SummaryCards({ cards, isLoading, action }) {
  const accent = '#D53541';

  return (
    <Box
      sx={{
        alignItems: 'stretch',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        mb: 3,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          flex: 1,
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: `repeat(${Math.min(cards.length, 2)}, minmax(0, 1fr))`,
            md: 'repeat(auto-fit, minmax(220px, 1fr))',
          },
          minWidth: 0,
        }}
      >
        {cards.map((card) => (
          <Paper
            key={card.label}
            sx={{
              alignItems: 'center',
              border: `1px solid ${accent}66`,
              borderTop: `4px solid ${accent}`,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              justifyContent: 'center',
              minHeight: 132,
              minWidth: 0,
              p: 2.5,
              textAlign: 'center',
              width: '100%',
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                color: `${accent}`,
                fontWeight: 700,
                mb: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {card.label}
            </Typography>
            {isLoading ? (
              <Skeleton
                animation="wave"
                variant="text"
                sx={{
                  bgcolor: 'rgba(213, 53, 65, 0.18)',
                  borderRadius: 1,
                  fontSize: { xs: '1.75rem', md: 'clamp(1.2rem, 1.55vw, 1.85rem)' },
                  width: '78%',
                }}
              />
            ) : (
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: '1.75rem', md: 'clamp(1.2rem, 1.55vw, 1.85rem)' },
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {card.value}
              </Typography>
            )}
          </Paper>
        ))}
      </Box>
      {action && <Box sx={{ flexShrink: 0, minWidth: { xs: '100%', md: 240 } }}>{action}</Box>}
    </Box>
  );
}

function Statistics() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [topPlaysSortBy, setTopPlaysSortBy] = useState('income');
  const [topPlaysVisibleCount, setTopPlaysVisibleCount] = useState(TOP_PLAYS_PAGE_SIZE);
  const [topEventsSortBy, setTopEventsSortBy] = useState('income');
  const [topEventsVisibleCount, setTopEventsVisibleCount] = useState(TOP_PLAYS_PAGE_SIZE);
  const [selectedSummaryChannels, setSelectedSummaryChannels] = useState(channelKeys);
  const [openSaleReports, setSaleReportsOpen] = useState(false);
  const hasLoadedInitialStatisticsRef = useRef(false);
  const {
    data,
    events,
    eventsCurrentPage,
    eventsHasMore,
    isLoading,
    isLoadingEvents,
    isLoadingTopPerformers,
    options,
    topPerformersData,
  } = useSelector((state) => state.orderStatistics);
  const [draftOptions, setDraftOptions] = useState(options);
  const user = useSelector((state) => state.auth.user);
  const organizers = useSelector((state) => state.organizers.data);
  const isLoadingOrganizers = useSelector((state) => state.organizers.isLoading);
  const isAdmin = user.role === 'admin';
  const isCashier = user.role === 'cashier';
  const activeOrganizers = useMemo(
    () => organizers.filter((organizer) => String(organizer.status).toUpperCase() === 'ACTIVE'),
    [organizers]
  );
  const draftOrganizerId = isAdmin ? draftOptions.organizerId || null : isCashier ? null : user.organizer_id;
  const paymentPointId = isCashier ? user.payment_point_id : null;

  const from = draftOptions.from ? dayjs(draftOptions.from) : null;
  const to = draftOptions.to ? dayjs(draftOptions.to) : null;
  const selectedEvent = useMemo(
    () => events.find((event) => String(event.id) === String(draftOptions.eventId)) || null,
    [draftOptions.eventId, events]
  );
  const eventSalesStart = useMemo(() => getEventSalesStart(selectedEvent), [selectedEvent]);
  const eventSalesEnd = useMemo(() => getEventSalesEnd(selectedEvent), [selectedEvent]);
  const shouldApplyMaxRange = !selectedEvent;
  const fromMinDate = maxDay(
    eventSalesStart,
    shouldApplyMaxRange && to ? to.subtract(MAX_RANGE_MONTHS, 'month') : null
  );
  const fromMaxDate = minDay(to, eventSalesEnd);
  const toMinDate = maxDay(from, eventSalesStart);
  const toMaxDate = minDay(eventSalesEnd, shouldApplyMaxRange && from ? from.add(MAX_RANGE_MONTHS, 'month') : null);
  const hasValidRange =
    !!from &&
    !!to &&
    !to.isBefore(from, 'day') &&
    (!shouldApplyMaxRange || !to.isAfter(from.add(MAX_RANGE_MONTHS, 'month'), 'day')) &&
    (!eventSalesStart || !from.isBefore(eventSalesStart, 'day')) &&
    (!eventSalesEnd || !to.isAfter(eventSalesEnd, 'day'));
  const hasValidFilters = hasValidRange && (isAdmin || !!draftOrganizerId || !!paymentPointId);
  const statisticsOptions = useMemo(
    () => ({
      ...draftOptions,
      organizerId: draftOrganizerId,
      paymentPointId,
      eventId: draftOptions.eventId,
    }),
    [draftOptions, draftOrganizerId, paymentPointId]
  );

  useEffect(() => {
    dispatch(clearOrderStatisticsEvents());
    dispatch(fetchOrderStatisticsEvents({ page: 1, limit: EVENT_SELECT_PAGE_SIZE, organizerId: draftOrganizerId }));
  }, [dispatch, draftOrganizerId]);

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchOrganizers());
    }
  }, [dispatch, isAdmin]);

  useEffect(() => {
    if (hasLoadedInitialStatisticsRef.current || !hasValidFilters) return;

    hasLoadedInitialStatisticsRef.current = true;
    dispatch(setOrderStatisticsDateRange(draftOptions));
    dispatch(fetchOrganizerTicketSalesStatistics(statisticsOptions));
    if (!isCashier) {
      dispatch(
        fetchOrganizerTopPerformersStatistics({
          organizerId: draftOrganizerId,
          paymentPointId,
          from: draftOptions.from,
          to: draftOptions.to,
        })
      );
    }
  }, [dispatch, draftOptions, draftOrganizerId, hasValidFilters, isCashier, paymentPointId, statisticsOptions]);

  useEffect(() => {
    if (!from || !to) return;

    const patch = {};

    if (eventSalesStart && from.isBefore(eventSalesStart, 'day')) {
      patch.from = eventSalesStart.format(DATE_FORMAT);
    }

    if (eventSalesEnd && to.isAfter(eventSalesEnd, 'day')) {
      patch.to = eventSalesEnd.format(DATE_FORMAT);
    }

    const effectiveFrom = patch.from ? dayjs(patch.from) : from;
    const effectiveTo = patch.to ? dayjs(patch.to) : to;

    if (
      shouldApplyMaxRange &&
      effectiveFrom &&
      effectiveTo &&
      effectiveTo.isAfter(effectiveFrom.add(MAX_RANGE_MONTHS, 'month'), 'day')
    ) {
      patch.to = effectiveFrom.add(MAX_RANGE_MONTHS, 'month').format(DATE_FORMAT);
    }

    if (patch.from && patch.to && dayjs(patch.to).isBefore(dayjs(patch.from), 'day')) {
      patch.from = patch.to;
    } else if (patch.from && to.isBefore(dayjs(patch.from), 'day')) {
      patch.to = patch.from;
    } else if (patch.to && from.isAfter(dayjs(patch.to), 'day')) {
      patch.from = patch.to;
    }

    if (Object.keys(patch).length) {
      setDraftOptions((current) => ({ ...current, ...patch }));
    }
  }, [eventSalesEnd, eventSalesStart, from, shouldApplyMaxRange, to]);

  const { rows, labels } = useMemo(() => buildTimeSeriesRows(normalizePoints(data), 'tickets'), [data]);

  const ticketYAxisMax = useMemo(() => getSuggestedYAxisMax(rows.map((row) => row.tickets)), [rows]);

  const { minDate, maxDate } = useMemo(() => getDateDomain(labels), [labels]);

  const chartDomainKey = useMemo(
    () => `${minDate ?? 'empty'}-${maxDate ?? 'empty'}-${labels.length}-${ticketYAxisMax}`,
    [labels.length, maxDate, minDate, ticketYAxisMax]
  );

  const chartData = useMemo(
    () =>
      buildTimeSeriesDatasets({
        rows,
        labels,
        valueKey: 'tickets',
        t,
        isCashier,
        datasetOptions: {
          barThickness: 15,
          maxBarThickness: 15,
        },
      }),
    [isCashier, labels, rows, t]
  );

  const { rows: incomeRows, labels: incomeLabels } = useMemo(
    () => buildTimeSeriesRows(normalizeIncomePoints(data), 'income'),
    [data]
  );

  const incomeYAxisMax = useMemo(() => getSuggestedYAxisMax(incomeRows.map((row) => row.income)), [incomeRows]);
  const { rows: comissionRows, labels: comissionLabels } = useMemo(
    () => buildTimeSeriesRows(normalizeComissionPoints(data), 'comission'),
    [data]
  );

  const comissionYAxisMax = useMemo(
    () => getSuggestedYAxisMax(comissionRows.map((row) => row.comission)),
    [comissionRows]
  );

  const { rows: orderRows, labels: orderLabels } = useMemo(
    () => buildTimeSeriesRows(normalizeOrderPoints(data), 'orders'),
    [data]
  );

  const orderYAxisMax = useMemo(() => getSuggestedYAxisMax(orderRows.map((row) => row.orders)), [orderRows]);
  const summaryChannels = isCashier ? ['cashier'] : selectedSummaryChannels;
  const totalIncome = getFilteredTotal(data?.totals?.income, summaryChannels);
  const totalTickets = getFilteredTotal(data?.totals?.tickets ?? data?.totals, summaryChannels);
  const totalOrders = getFilteredTotal(data?.totals?.orders, summaryChannels);
  const totalRefunds = getFilteredTotal(data?.totals?.refunds?.tickets ?? data?.totals?.refunds, summaryChannels);
  const totalComission = getFilteredTotal(data?.totals?.comission, summaryChannels);
  const summaryCards = useMemo(() => {
    const cards = [
      {
        label: t('Income'),
        value: formatMoney(totalIncome, 'RON', i18n),
      },
      ...(!isCashier
        ? [
            {
              label: t('Commission'),
              value: formatMoney(totalComission, 'RON', i18n),
            },
          ]
        : []),
      {
        label: t('Orders'),
        value: formatCount(totalOrders),
      },
      {
        label: t('Tickets'),
        value: formatCount(totalTickets),
      },
      {
        label: t('Refunds'),
        value: formatCount(totalRefunds),
      },
    ];

    return cards;
  }, [i18n, isCashier, t, totalComission, totalIncome, totalOrders, totalRefunds, totalTickets]);

  const { minDate: orderMinDate, maxDate: orderMaxDate } = useMemo(() => getDateDomain(orderLabels), [orderLabels]);

  const orderChartDomainKey = useMemo(
    () => `${orderMinDate ?? 'empty'}-${orderMaxDate ?? 'empty'}-${orderLabels.length}-${orderYAxisMax}`,
    [orderLabels.length, orderMaxDate, orderMinDate, orderYAxisMax]
  );

  const { minDate: incomeMinDate, maxDate: incomeMaxDate } = useMemo(() => getDateDomain(incomeLabels), [incomeLabels]);

  const incomeChartDomainKey = useMemo(
    () => `${incomeMinDate ?? 'empty'}-${incomeMaxDate ?? 'empty'}-${incomeLabels.length}-${incomeYAxisMax}`,
    [incomeLabels.length, incomeMaxDate, incomeMinDate, incomeYAxisMax]
  );

  const { minDate: comissionMinDate, maxDate: comissionMaxDate } = useMemo(
    () => getDateDomain(comissionLabels),
    [comissionLabels]
  );

  const comissionChartDomainKey = useMemo(
    () =>
      `${comissionMinDate ?? 'empty'}-${comissionMaxDate ?? 'empty'}-${comissionLabels.length}-${comissionYAxisMax}`,
    [comissionLabels.length, comissionMaxDate, comissionMinDate, comissionYAxisMax]
  );

  const incomeChartData = useMemo(
    () =>
      buildTimeSeriesDatasets({
        rows: incomeRows,
        labels: incomeLabels,
        valueKey: 'income',
        t,
        isCashier,
        filterChannel: (channel) => String(channel).toUpperCase() !== 'ORGANIZER',
        getLabel: (channel) => (channel === 'total' ? t('Income') : formatChannel(channel, t)),
      }),
    [incomeLabels, incomeRows, isCashier, t]
  );

  const comissionChartData = useMemo(
    () =>
      buildTimeSeriesDatasets({
        rows: comissionRows,
        labels: comissionLabels,
        valueKey: 'comission',
        t,
        isCashier,
        getLabel: (channel) => (channel === 'total' ? t('Commission') : formatChannel(channel, t)),
      }),
    [comissionLabels, comissionRows, isCashier, t]
  );

  const orderChartData = useMemo(
    () =>
      buildTimeSeriesDatasets({
        rows: orderRows,
        labels: orderLabels,
        valueKey: 'orders',
        t,
        isCashier,
      }),
    [isCashier, orderLabels, orderRows, t]
  );

  const topPlayRows = useMemo(() => {
    const rows = getTopPlayRows(topPerformersData);

    return rows.sort((a, b) => {
      const valueDiff = topPlaysSortBy === 'income' ? b.income - a.income : b.tickets - a.tickets;
      if (valueDiff !== 0) return valueDiff;
      return String(a.title).localeCompare(String(b.title));
    });
  }, [topPerformersData, topPlaysSortBy]);

  const topEventRows = useMemo(() => {
    const rows = getTopEventRows(topPerformersData);

    return rows.sort((a, b) => {
      const valueDiff = topEventsSortBy === 'income' ? b.income - a.income : b.tickets - a.tickets;
      if (valueDiff !== 0) return valueDiff;
      return String(a.title).localeCompare(String(b.title));
    });
  }, [topPerformersData, topEventsSortBy]);

  useEffect(() => {
    setTopPlaysVisibleCount(TOP_PLAYS_PAGE_SIZE);
  }, [topPerformersData, topPlaysSortBy]);

  useEffect(() => {
    setTopEventsVisibleCount(TOP_PLAYS_PAGE_SIZE);
  }, [topPerformersData, topEventsSortBy]);

  const cashierPaymentMethodRows = useMemo(() => getCashierPaymentMethodRows(data), [data]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          min: 0,
          beginAtZero: true,
          suggestedMax: ticketYAxisMax,
          ticks: {
            color: 'white',
            precision: 0,
          },
          grid: { color: 'rgba(182, 186, 182, 0.3)' },
          border: { display: false },
        },
        x: {
          type: 'time',
          min: minDate ?? undefined,
          max: maxDate ?? undefined,
          ticks: {
            color: 'white',
            maxTicksLimit: 15,
          },
          grid: { display: false },
          border: { display: false },
          time: {
            unit: 'day',
            displayFormats: {
              day: 'DD/MM',
            },
            tooltipFormat: 'DD/MM/YYYY',
          },
        },
      },
      plugins: {
        legend: {
          display: !isCashier,
          position: 'bottom',
          labels: {
            color: 'white',
            usePointStyle: true,
          },
        },
        zoom: {
          pan: {
            enabled: true,
            mode: 'x',
          },
          zoom: {
            wheel: {
              enabled: false,
            },
            pinch: {
              enabled: true,
            },
            mode: 'x',
          },
          limits: {
            x: {
              min: 'original',
              max: 'original',
              minRange: SEVEN_DAYS,
            },
            y: {
              min: 0,
            },
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${ctx.formattedValue} ${t('tickets')}`,
          },
        },
      },
    }),
    [isCashier, maxDate, minDate, t, ticketYAxisMax]
  );

  const incomeChartOptions = useMemo(
    () => ({
      ...chartOptions,
      scales: {
        ...chartOptions.scales,
        x: {
          ...chartOptions.scales.x,
          min: incomeMinDate ?? undefined,
          max: incomeMaxDate ?? undefined,
        },
        y: {
          ...chartOptions.scales.y,
          suggestedMax: incomeYAxisMax,
          ticks: {
            ...chartOptions.scales.y.ticks,
            callback: (value) => formatMoney(value, 'RON', i18n),
          },
        },
      },
      plugins: {
        ...chartOptions.plugins,
        legend: {
          ...chartOptions.plugins.legend,
          display: !isCashier,
        },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${formatMoney(ctx.parsed.y, 'RON', i18n)}`,
          },
        },
      },
    }),
    [chartOptions, i18n, incomeMaxDate, incomeMinDate, incomeYAxisMax, isCashier]
  );

  const orderChartOptions = useMemo(
    () => ({
      ...chartOptions,
      scales: {
        ...chartOptions.scales,
        x: {
          ...chartOptions.scales.x,
          min: orderMinDate ?? undefined,
          max: orderMaxDate ?? undefined,
        },
        y: {
          ...chartOptions.scales.y,
          suggestedMax: orderYAxisMax,
        },
      },
      plugins: {
        ...chartOptions.plugins,
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${ctx.formattedValue} ${t('orders')}`,
          },
        },
      },
    }),
    [chartOptions, orderMaxDate, orderMinDate, orderYAxisMax, t]
  );

  const comissionChartOptions = useMemo(
    () => ({
      ...chartOptions,
      scales: {
        ...chartOptions.scales,
        x: {
          ...chartOptions.scales.x,
          min: comissionMinDate ?? undefined,
          max: comissionMaxDate ?? undefined,
        },
        y: {
          ...chartOptions.scales.y,
          suggestedMax: comissionYAxisMax,
          ticks: {
            ...chartOptions.scales.y.ticks,
            callback: (value) => formatMoney(value, 'RON', i18n),
          },
        },
      },
      plugins: {
        legend: {
          ...chartOptions.plugins.legend,
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${formatMoney(ctx.parsed.y, 'RON', i18n)}`,
          },
        },
      },
    }),
    [chartOptions, comissionMaxDate, comissionMinDate, comissionYAxisMax, i18n]
  );

  const handleDateChange = (key) => (value) => {
    setDraftOptions((current) => ({
      ...current,
      [key]: value ? dayjs(value).format(DATE_FORMAT) : null,
    }));
  };

  const handleSummaryChannelsChange = (event) => {
    const value = event.target.value;
    const nextChannels = typeof value === 'string' ? value.split(',') : value;

    if (nextChannels.length === 0) return;

    setSelectedSummaryChannels(nextChannels);
  };

  const toggleOpenSaleReports = () => setSaleReportsOpen((open) => !open);

  const handleEventChange = (event) => {
    const eventId = event.target.value;
    const selected = events.find((item) => String(item.id) === String(eventId));
    const salesStart = getEventSalesStart(selected);
    const salesEnd = getEventSalesEnd(selected);

    setDraftOptions((current) => ({
      ...current,
      eventId,
      ...(salesStart ? { from: salesStart.format(DATE_FORMAT) } : {}),
      ...(salesEnd ? { to: salesEnd.format(DATE_FORMAT) } : {}),
    }));
  };

  const handleFilter = () => {
    dispatch(setOrderStatisticsDateRange(draftOptions));
    dispatch(fetchOrganizerTicketSalesStatistics(statisticsOptions));
    if (!isCashier) {
      dispatch(
        fetchOrganizerTopPerformersStatistics({
          organizerId: draftOrganizerId,
          paymentPointId,
          from: draftOptions.from,
          to: draftOptions.to,
        })
      );
    }
  };

  const handleEventMenuScroll = (event) => {
    const target = event.currentTarget;

    if (target.scrollTop + target.clientHeight < target.scrollHeight - 24) return;

    if (!eventsHasMore || isLoadingEvents) return;

    dispatch(
      fetchOrderStatisticsEvents({
        page: eventsCurrentPage + 1,
        limit: EVENT_SELECT_PAGE_SIZE,
        organizerId: draftOrganizerId,
      })
    );
  };

  const summaryChannelSelector = !isCashier ? (
    <FormControl fullWidth size="small">
      <InputLabel id="summary-channel-filter-label" shrink>
        {t('Roles')}
      </InputLabel>
      <Select
        labelId="summary-channel-filter-label"
        label={t('Roles')}
        multiple
        value={selectedSummaryChannels}
        onChange={handleSummaryChannelsChange}
        renderValue={(selected) =>
          selected.length === channelKeys.length
            ? t('All roles')
            : selected.map((channel) => formatChannel(channel, t)).join(', ')
        }
      >
        {channelKeys.map((channel) => (
          <MenuItem
            key={channel}
            value={channel}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'transparent',
              },
              '&.Mui-selected:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <Checkbox checked={selectedSummaryChannels.includes(channel)} />
            <ListItemText primary={formatChannel(channel, t)} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  ) : null;

  return (
    <Box>
      <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
        <Grid item>
          <Typography variant="h3">{t('Statistics')}</Typography>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={toggleOpenSaleReports}>
            {t('Sale Reports')}
          </Button>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mt: 4, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {isAdmin && (
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel id="statistics-organizer-filter-label" shrink>
                  {t('Organizer')}
                </InputLabel>
                <Select
                  labelId="statistics-organizer-filter-label"
                  label={t('Organizer')}
                  displayEmpty
                  value={draftOptions.organizerId || ''}
                  disabled={isLoadingOrganizers}
                  onChange={(event) =>
                    setDraftOptions((current) => ({
                      ...current,
                      organizerId: event.target.value,
                      eventId: '',
                    }))
                  }
                  renderValue={(value) => {
                    if (!value) return t('All organizers');

                    const selected = activeOrganizers.find((organizer) => String(organizer.id) === String(value));

                    return selected ? selected.name : value;
                  }}
                >
                  <MenuItem value="">{t('All organizers')}</MenuItem>
                  {activeOrganizers.map((organizer) => (
                    <MenuItem key={organizer.id} value={organizer.id}>
                      {organizer.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="statistics-event-filter-label" shrink>
                {t('Event')}
              </InputLabel>
              <Select
                labelId="statistics-event-filter-label"
                label={t('Event')}
                displayEmpty
                value={draftOptions.eventId ? String(draftOptions.eventId) : ''}
                disabled={isLoadingEvents}
                onChange={handleEventChange}
                renderValue={(value) => {
                  if (!value) return t('All');

                  const selected = events.find((event) => String(event.id) === String(value));

                  return selected ? getEventLabel(selected) : value;
                }}
                MenuProps={{
                  PaperProps: {
                    onScroll: handleEventMenuScroll,
                    sx: { maxHeight: 360 },
                  },
                }}
              >
                <MenuItem value="">{t('All')}</MenuItem>
                {events.map((event) => (
                  <MenuItem key={event.id} value={String(event.id)}>
                    {getEventLabel(event)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={isAdmin ? 2 : 3}>
            <DatePicker
              disableFuture
              minDate={fromMinDate || undefined}
              maxDate={fromMaxDate || undefined}
              value={from}
              onChange={handleDateChange('from')}
              slotProps={{ textField: { label: t('From'), fullWidth: true, variant: 'outlined', error: false } }}
            />
          </Grid>
          <Grid item xs={12} md={isAdmin ? 2 : 3}>
            <DatePicker
              disableFuture
              minDate={toMinDate || undefined}
              maxDate={toMaxDate || undefined}
              value={to}
              onChange={handleDateChange('to')}
              slotProps={{ textField: { label: t('To'), fullWidth: true, variant: 'outlined', error: false } }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
            >
              <Button
                variant="contained"
                disabled={!hasValidFilters || isLoading || (!isCashier && isLoadingTopPerformers)}
                onClick={handleFilter}
              >
                {t('Filter')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <SummaryCards cards={summaryCards} isLoading={isLoading} action={summaryChannelSelector} />

      <StatisticsLineChart
        title={t('Tickets')}
        type="bar"
        data={chartData}
        options={chartOptions}
        isLoading={isLoading}
        isEmpty={rows.length === 0}
        minDate={minDate}
        maxDate={maxDate}
        domainKey={chartDomainKey}
      />

      <StatisticsLineChart
        title={t('Orders')}
        data={orderChartData}
        options={orderChartOptions}
        isLoading={isLoading}
        isEmpty={orderRows.length === 0}
        minDate={orderMinDate}
        maxDate={orderMaxDate}
        domainKey={orderChartDomainKey}
      />

      <StatisticsLineChart
        title={t('Income')}
        data={incomeChartData}
        options={incomeChartOptions}
        isLoading={isLoading}
        isEmpty={incomeRows.length === 0}
        minDate={incomeMinDate}
        maxDate={incomeMaxDate}
        domainKey={incomeChartDomainKey}
      />

      {!isCashier && (
        <StatisticsLineChart
          title={t('Commission')}
          data={comissionChartData}
          options={comissionChartOptions}
          isLoading={isLoading}
          isEmpty={comissionRows.length === 0}
          minDate={comissionMinDate}
          maxDate={comissionMaxDate}
          domainKey={comissionChartDomainKey}
        />
      )}

      {isCashier ? (
        <CashierPaymentMethodsPieChart rows={cashierPaymentMethodRows} isLoading={isLoading} />
      ) : (
        <>
          <TopPerformersChart
            title={t('Top plays')}
            rows={topPlayRows}
            sortBy={topPlaysSortBy}
            onSortByChange={setTopPlaysSortBy}
            visibleCount={topPlaysVisibleCount}
            onLoadMore={() => setTopPlaysVisibleCount((count) => count + TOP_PLAYS_PAGE_SIZE)}
            isLoading={isLoadingTopPerformers}
          />
          <TopPerformersChart
            title={t('Top events')}
            rows={topEventRows}
            sortBy={topEventsSortBy}
            onSortByChange={setTopEventsSortBy}
            visibleCount={topEventsVisibleCount}
            onLoadMore={() => setTopEventsVisibleCount((count) => count + TOP_PLAYS_PAGE_SIZE)}
            isLoading={isLoadingTopPerformers}
          />
        </>
      )}
      <SaleReportsDialog open={openSaleReports} onClose={toggleOpenSaleReports} />
    </Box>
  );
}

export default Statistics;
